import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUserSettings } from '../../recoil/selectors/permissionsSelector';
import {
  IAccessTokenJson,
  setCachedAccessToken,
} from '../../api/checkAndGetNewAccessToken';
import { RootState } from '../../app/store';

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const saveAccessTokenJson = createAsyncThunk<
  IAccessTokenJson | undefined,
  IAccessTokenJson | undefined,
  { state: RootState }
>('user/saveAccessTokenJson', async (accessTokenJson, thunkAPI) => {
  // set/remove access token cookies
  setCachedAccessToken(accessTokenJson);
  return accessTokenJson;
});

interface IUserState {
  saveAccessTokenJson?: 'pending' | 'fulfilled' | 'rejected';
  settings?: IUserSettings;
  accessTokenJson?: IAccessTokenJson;
}

const initialUserState: IUserState = {};

export const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    /** Replace user.settings with action.payload */
    replaceUserSettings(state, action: PayloadAction<IUserState['settings']>) {
      // Redux Toolkit allows us to write "mutating" logic in reducers. It
      // doesn't actually mutate the state because it uses the Immer library,
      // which detects changes to a "draft state" and produces a brand new
      // immutable state based off those changes
      state.settings = action.payload;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(saveAccessTokenJson.pending, (state) => {
        state.saveAccessTokenJson = 'pending';
      })
      .addCase(saveAccessTokenJson.fulfilled, (state, action) => {
        state.saveAccessTokenJson = action.payload ? 'fulfilled' : 'rejected';
        state.accessTokenJson = action.payload;
      })
      .addCase(saveAccessTokenJson.rejected, (state) => {
        state.saveAccessTokenJson = 'rejected';
      });
  },
});

// actions from thunk: saveAccessTokenJson
export const { replaceUserSettings } = userSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.plivo.value)`
export const selectUserSettings = (state: RootState) => state.user.settings;
export const selectAccessTokenJson = (state: RootState) =>
  state.user.accessTokenJson;

export const userReducer = userSlice.reducer;
